const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const formatEmailText = (value) => {
  const safeValue = escapeHtml(value)
  return safeValue ? safeValue.replace(/\r?\n/g, '<br />') : '&nbsp;'
}

const yesNo = (value) => (value ? 'Yes' : 'No')

const formatEnumValue = (value, options = {}) => {
  const key = String(value || '').trim()
  if (!key) return ''
  return options[key] ? `${key} - ${options[key]}` : key
}

const toList = (value, options = {}) => {
  if (Array.isArray(value)) {
    return value.map((item) => options[item] || item).filter(Boolean)
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .filter((key) => value[key])
      .map((key) => options[key] || key)
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => options[item] || item)
  }
  if (value) {
    return [options[value] || value]
  }
  return []
}

const EXUDATE_AMOUNT = {
  1: 'Low',
  2: 'Moderate',
  3: 'High',
}

const DIABETIC_STAGE = {
  A0: 'Pre- or post-ulcerative lesion completely epithelialized',
  A1: 'Superficial wound not involving tendon',
  A2: 'Wound penetrating to tendon or capsule',
  A3: 'Wound penetrating to bone or joint',
  B0: 'Infection (low grade)',
  B1: 'Infection (moderate grade)',
  B2: 'Infection (medium - to high grade)',
  B3: 'Infection (severe grade)',
  C0: 'Ischemia (low grade)',
  C1: 'Ischemia (moderate grade)',
  C2: 'Ischemia (medium to high grade)',
  C3: 'Ischemia (severe grade)',
  D0: 'Infection and Ischemia (low grade)',
  D1: 'Infection and Ischemia (moderate grade)',
  D2: 'Infection and Ischemia (medium to high grade)',
  D3: 'Infection and Ischemia (severe grade)',
}

const PRESSURE_STAGE = {
  1: 'Stage 1 - Non-blanchable erythema',
  2: 'Stage 2 - Partial-thickness skin loss',
  3: 'Stage 3 - Full-thickness skin loss',
  4: 'Stage 4 - Full-thickness tissue loss',
  5: 'Unstageable - Obscured full-thickness loss base',
  6: 'DTPI - Deep tissue pressure injury',
}

const VENOUS_STAGE = {
  1: 'C0 - No visible or palpable signs of venous disease',
  2: 'C1 - Telangiectasias or reticular veins',
  3: 'C2 - Varicose veins',
  4: 'C3 - Oedema',
  5: 'C4a - Pigmentation and/or eczema',
  6: 'C4b - Lipodermatosclerosis and/or atrophie blanche',
  7: 'C5 - Healed venous ulcer',
  8: 'C6 - Active venous ulcer',
}

const NERDS_MAP = {
  neon: 'Neon',
  effervescent: 'Effervescent',
  rich: 'Rich',
  drainage: 'Drainage',
  scintillating: 'Scintillating',
}

const STONES_MAP = {
  size: 'Size is bigger',
  temperature: 'Temperature is increased',
  os: 'Os (probe to bone)',
  newareas: 'New or satellite areas of breakdown',
  exudate: 'Exudate',
  erythema: 'Erythema / edema',
  smell: 'Smell',
}

export const buildAuthorizationAssessmentRows = (assessment = {}, options = {}) => {
  const { omitWoundSpecific = false } = options
  if (omitWoundSpecific) {
    return []
  }

  const rows = []
  const push = (key, value) => {
    rows.push({ key, value: value ?? '' })
  }
  const pushIfValue = (key, value) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      push(key, value)
    }
  }

  const woundTypes = [
    assessment.postsurgical ? 'Surgical wound' : '',
    assessment.diabeticUlcer ? 'Diabetic ulcer' : '',
    assessment.pressureUlcer ? 'Pressure ulcer' : '',
    assessment.venousUlcer ? 'Venous ulcer' : '',
    assessment.trauma ? 'Trauma' : '',
    assessment.burns ? 'Burns' : '',
    (assessment.cellulitis || assessment.infection) ? 'Cellulitis' : '',
    assessment.other ? 'Other' : '',
  ].filter(Boolean)

  const traumaDetails =
    typeof assessment.trauma === 'string'
      ? assessment.trauma
      : (assessment.traumaDesc || assessment.traumaFindings || assessment.traumaDetails || '')

  const burnDegrees = [
    assessment.burnDegree1 ? 'First degree' : '',
    assessment.burnDegree2 || assessment.burndegree2 ? 'Second degree' : '',
    assessment.burnDegree3 ? 'Third degree' : '',
  ].filter(Boolean)

  pushIfValue('Wound onset date:', assessment.woundStartDate)
  pushIfValue('Treatment start date:', assessment.treatmentStartDate)
  pushIfValue('Location of wound:', assessment.woundLocation)
  if (!omitWoundSpecific) {
    push('Wound description:', woundTypes.length > 0 ? woundTypes.join(', ') : 'None selected')
  }

  if (assessment.postsurgical) {
    pushIfValue('Surgical procedure:', assessment.surgicalProc)
  }
  if (!omitWoundSpecific && assessment.diabeticUlcer) {
    pushIfValue('Texas University Classification:', formatEnumValue(assessment.dstage, DIABETIC_STAGE))
  }
  if (assessment.pressureUlcer) {
    pushIfValue('Pressure ulcer stage:', formatEnumValue(assessment.pstage, PRESSURE_STAGE))
  }
  if (assessment.venousUlcer) {
    pushIfValue('CEAP Classification:', formatEnumValue(assessment.vstage, VENOUS_STAGE))
  }
  if (assessment.trauma) {
    pushIfValue('Trauma diagnostic findings:', traumaDetails)
  }
  if (assessment.burns) {
    pushIfValue('Burn degree:', burnDegrees.join(', '))
    pushIfValue('Total body surface area (%):', assessment.burnArea)
  }
  if (assessment.cellulitis || assessment.infection) {
    push('Colonised:', yesNo(assessment.colonised))
    push('Superficial:', yesNo(assessment.superficial))

    const nerds = toList(assessment.nerds, NERDS_MAP)
    const stones = toList(assessment.stones, STONES_MAP)
    pushIfValue('Superficial indicators (NERDS):', nerds.join(', '))
    pushIfValue('Deep infection indicators (STONES):', stones.join(', '))

    pushIfValue('Exudate - Serous:', formatEnumValue(assessment.serous, EXUDATE_AMOUNT))
    pushIfValue('Exudate - Sanguineous:', formatEnumValue(assessment.sanguineous, EXUDATE_AMOUNT))
    pushIfValue('Exudate - Serosanguineous:', formatEnumValue(assessment.serosang, EXUDATE_AMOUNT))
    pushIfValue('Exudate - Purulent:', formatEnumValue(assessment.purulent, EXUDATE_AMOUNT))
  }
  if (assessment.other) {
    pushIfValue('Other wound type description:', assessment.otherDesc)
  }

  if (!omitWoundSpecific) {
    push('Cellulitis:', yesNo(assessment.cellulitis || assessment.infection))
    push('Inflammation:', yesNo(assessment.inflammation))
    pushIfValue('Moisture balance:', assessment.balance)
    pushIfValue('Tissue viability:', assessment.viability)
    pushIfValue('Pain measurement (0-10):', assessment.painLevel)
  }
  pushIfValue('Previous conservative treatments:', assessment.treatment)
  pushIfValue('Treatment plan / advanced wound care:', assessment.treatmentPlan)

  return rows
}

export const buildInvoiceEmailHtml = ({
  dateString,
  groupDetails,
  patient,
  prodList,
  totals,
  logoInlineAttachment,
}) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'ZAR',
  })

  const rowsHtml = (prodList || []).map((item) => `
    <tr>
      <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${escapeHtml(item.date)}</td>
      <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${escapeHtml(item.tariffCode)}</td>
      <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${escapeHtml(item.nappiCode)}</td>
      <td style="border:1px solid #d9d9d9;padding:8px;">${escapeHtml(item.title)}</td>
      <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${escapeHtml(item.ICD10Codes)}</td>
      <td style="border:1px solid #d9d9d9;padding:8px;text-align:right;white-space:nowrap;">${escapeHtml(item.quantity)}</td>
      <td style="border:1px solid #d9d9d9;padding:8px;text-align:right;white-space:nowrap;">${escapeHtml(item.amount)}</td>
    </tr>
  `).join('')

  const safeRowsHtml = rowsHtml || `
    <tr>
      <td colspan="7" style="border:1px solid #d9d9d9;padding:10px;text-align:center;color:#666;">No invoice line items</td>
    </tr>
  `

  return `
    <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body style="margin:0;padding:16px;background:#f3f5f8;font-family:Arial,sans-serif;color:#1f2937;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:980px;margin:0 auto;background:#ffffff;border:1px solid #d9d9d9;">
          <tr>
            <td style="padding:16px 24px 0 24px;font-size:14px;line-height:1.6;color:#374151;">
              Hello, please find the invoice for ${escapeHtml(patient.firstName)} ${escapeHtml(patient.patientName)}. Kind regards.
            </td>
          </tr>
          <tr>
            <td style="padding:20px 24px;border-bottom:1px solid #d9d9d9;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td style="vertical-align:top;">
                    <div style="font-size:20px;font-weight:700;line-height:1.4;">${escapeHtml(groupDetails.hdetail1)}</div>
                    <div style="font-size:16px;line-height:1.4;">${escapeHtml(groupDetails.hdetail2)}</div>
                    <div style="font-size:14px;line-height:1.4;">${escapeHtml(groupDetails.hdetail3)}</div>
                    <div style="font-size:13px;line-height:1.6;margin-top:10px;">${escapeHtml(groupDetails.hdetail4)}</div>
                    <div style="font-size:13px;line-height:1.6;"><strong>${escapeHtml(groupDetails.hdetail5)}</strong> ${escapeHtml(groupDetails.hdetail6)}</div>
                    <div style="font-size:13px;line-height:1.6;"><strong>${escapeHtml(groupDetails.hdetail7)}</strong> ${escapeHtml(groupDetails.hdetail8)}</div>
                    <div style="font-size:13px;line-height:1.6;"><strong>${escapeHtml(groupDetails.hdetail9)}</strong> ${escapeHtml(groupDetails.hdetail10)}</div>
                  </td>
                  <td style="text-align:right;vertical-align:top;">
                    ${logoInlineAttachment ? '<img src="cid:invoice-logo" alt="Logo" style="max-width:140px;height:auto;border:0;" />' : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:18px 24px 8px 24px;">
              <div style="font-size:20px;font-weight:700;margin-bottom:8px;">Tax Invoice</div>
              <div style="font-size:14px;line-height:1.7;">
                <div><strong>Invoice Date:</strong> ${escapeHtml(dateString)}</div>
                <div><strong>Patient:</strong> ${escapeHtml(patient.firstName)} ${escapeHtml(patient.patientName)}</div>
                <div><strong>Medical Aid:</strong> ${escapeHtml(patient.medicalAid)}</div>
                <div><strong>Member No:</strong> ${escapeHtml(patient.memberNo)}</div>
                <div><strong>Authorization No:</strong> ${escapeHtml(patient.authNo)}</div>
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding:0 24px 18px 24px;">
              <div style="font-size:13px;line-height:1.7;color:#374151;border-top:1px solid #e5e7eb;padding-top:12px;">
                <div><strong>Patient:</strong> ${escapeHtml(patient.firstName)} ${escapeHtml(patient.patientName)}, d.o.b: ${escapeHtml(patient.dateOfBirth)}, Dep Code: ${escapeHtml(patient.dependentCode)}</div>
                <div><strong>Treating Provider:</strong> ${escapeHtml(groupDetails.hdetail1)}, ${escapeHtml(groupDetails.hdetail11)}, Date of Service: ${escapeHtml(dateString)}</div>
                <div><strong>ICD10 Codes:</strong> ${escapeHtml(patient.ICD10Codes)}</div>
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding:8px 24px 24px 24px;">
              <table role="table" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;font-size:13px;">
                <thead>
                  <tr style="background:#2b4287;color:#ffffff;text-align:left;">
                    <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">Date of Service</th>
                    <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">Tariff Code</th>
                    <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">Nappi Code</th>
                    <th style="padding:10px;border:1px solid #1f3268;">Materials</th>
                    <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">ICD10 Codes</th>
                    <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;text-align:right;">Qty</th>
                    <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;text-align:right;">Cost (vat incl.)</th>
                  </tr>
                </thead>
                <tbody>
                  ${safeRowsHtml}
                </tbody>
              </table>
              <div style="text-align:right;margin-top:12px;font-size:16px;">
                <strong>Total:</strong> ${formatter.format(Number(totals || 0))}
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding:16px 24px 24px 24px;border-top:1px solid #d9d9d9;font-size:12px;line-height:1.6;color:#374151;">
              <div><strong>Bank Details:</strong> ${escapeHtml(groupDetails.bdetail1)}, ${escapeHtml(groupDetails.bdetail2)}, Branch ${escapeHtml(groupDetails.bdetail5)}, Account no: ${escapeHtml(groupDetails.bdetail3)}, Account Type: ${escapeHtml(groupDetails.bdetail4)}</div>
              <div><strong>Company Info:</strong> ${escapeHtml(groupDetails.cdetail1)}</div>
              <div><strong>EFT Info:</strong> ${escapeHtml(groupDetails.edetail1)} ${escapeHtml(patient.firstName)} ${escapeHtml(patient.patientName)} ${escapeHtml(groupDetails.edetail2)} ${escapeHtml(groupDetails.edetail3)}</div>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `
}

export const buildAuthorizationEmailHtml = ({
  assessment,
  dateString,
  fullName,
  groupDetails,
  images,
  patient,
  prodList,
  prodListColumns,
  supplRows1,
  supplRows2,
  logoInlineAttachment,
}) => {
  const defaultSupplRows2 = buildAuthorizationAssessmentRows(assessment || {})
  // If caller passes an array (including []), respect it and do not auto-fallback.
  const resolvedSupplRows2 = Array.isArray(supplRows2) ? supplRows2 : defaultSupplRows2

  const keyCellStyle = 'width:160px;text-align:left;font-weight:700;white-space:nowrap;border:1px solid #9aa3af;padding:8px 10px;vertical-align:top;background:#e6e8ed;'
  const valueCellStyle = 'text-align:left;border:1px solid #9aa3af;padding:8px 10px;vertical-align:top;background:#ffffff;line-height:1.5;'
  const prodHeaderStyleBase = 'border:1px solid #7f8897;padding:10px 8px;background:#c7ccd6;color:#1f2937;font-size:13px;font-weight:700;vertical-align:top;'
  const prodCellStyleBase = 'border:1px solid #9aa3af;padding:8px 10px;background:#ffffff;font-size:13px;vertical-align:top;'

  const supplRows1Html = (supplRows1 || []).map((row) => `
    <tr>
      <td style="${keyCellStyle}">${formatEmailText(row.key)}</td>
      <td style="${valueCellStyle}">${formatEmailText(row.value)}</td>
    </tr>
  `).join('')

  const prodHeaderHtml = (prodListColumns || []).map((column) => `
    <th style="${prodHeaderStyleBase}width:${column.width || 120}px;text-align:${column.align || 'left'};">
      ${escapeHtml(column.headerName)}
    </th>
  `).join('')

  const prodRowsHtml = (prodList || []).map((row) => `
    <tr>
      ${(prodListColumns || []).map((column) => {
        const rawValue = row[column.field] ?? ''
        const whiteSpace = column.field === 'title' ? 'normal' : 'nowrap'
        return `
          <td style="${prodCellStyleBase}width:${column.width || 120}px;text-align:${column.align || 'left'};white-space:${whiteSpace};">
            ${formatEmailText(rawValue)}
          </td>
        `
      }).join('')}
    </tr>
  `).join('')

  const safeProdRowsHtml = prodRowsHtml || `
    <tr>
      <td colspan="${(prodListColumns || []).length}" style="${prodCellStyleBase}text-align:center;color:#6b7280;">
        No authorization line items
      </td>
    </tr>
  `

  const supplRows2Html = (resolvedSupplRows2 || []).map((row) => `
    <tr>
      <td style="${keyCellStyle}">${formatEmailText(row.key)}</td>
      <td style="${valueCellStyle}">${formatEmailText(row.value)}</td>
    </tr>
  `).join('')

  const supplRows2SectionHtml = supplRows2Html
    ? `
          <tr>
            <td style="padding:0 24px 24px 24px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
                <tbody>
                  ${supplRows2Html}
                </tbody>
              </table>
            </td>
          </tr>
    `
    : ''

  const imagesHtml = (images || []).length > 0
    ? `
      <tr>
        <td style="padding:0 24px 24px 24px;">
          <div style="font-size:18px;font-weight:700;color:#1f2937;margin:0 0 12px 0;">Wound Images</div>
          ${(images || []).map((image) => `
            <div style="margin:0 0 18px 0;">
              <div style="font-size:13px;font-weight:700;color:#374151;margin:0 0 6px 0;">
                ${escapeHtml(image.title || 'Image')}
              </div>
              <img
                src="${escapeHtml(image.imageUrl || '')}"
                alt="${escapeHtml(image.title || 'Wound image')}"
                style="display:block;width:100%;max-width:900px;height:auto;border:1px solid #d9d9d9;background:#ffffff;"
              />
            </div>
          `).join('')}
        </td>
      </tr>
    `
    : ''

  return `
    <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body style="margin:0;padding:16px;background:#d2d5dc;font-family:Arial,sans-serif;color:#1f2937;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:980px;margin:0 auto;background:#ffffff;border:1px solid #9aa3af;">
          <tr>
            <td style="padding:18px 24px 0 24px;font-size:14px;line-height:1.6;color:#374151;">
              Hello, please find the authorization request for ${escapeHtml(patient.firstName)} ${escapeHtml(patient.patientName)}.
              <div style="color:#c73a3a;">
                Wound assessment and images incl. referral letter are attached in form of two separate PDF files. Quotation below.
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding:20px 24px;border-bottom:1px solid #d9d9d9;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td style="vertical-align:top;">
                    <div style="font-size:24px;font-weight:700;line-height:1.3;">
                      ${escapeHtml(groupDetails.hdetail1)} - ${escapeHtml(groupDetails.hdetail2)}
                    </div>
                    <div style="font-size:16px;line-height:1.4;">
                      ${escapeHtml(groupDetails.hdetail11)} / ${escapeHtml(groupDetails.hdetail12)}
                    </div>
                    <div style="font-size:13px;line-height:1.6;margin-top:10px;">
                      ${escapeHtml(groupDetails.hdetail5)}
                    </div>
                    <div style="font-size:13px;line-height:1.6;">
                      ${escapeHtml(groupDetails.hdetail7)}
                    </div>
                  </td>
                  <td style="text-align:right;vertical-align:top;">
                    ${logoInlineAttachment ? '<img src="cid:invoice-logo" alt="Logo" style="max-width:140px;height:auto;border:0;" />' : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:20px 24px 8px 24px;">
              <div style="font-size:24px;font-weight:700;line-height:1.3;margin-bottom:8px;">Authorization Request for wound care</div>
              <div style="font-size:14px;line-height:1.8;">
                <div><strong>To:</strong> ${escapeHtml(patient.medicalAid)}</div>
                <div><strong>Attention:</strong> Pre-Authorization</div>
                <div><strong>Name:</strong> ${escapeHtml(fullName)}</div>
                <div><strong>Contact Number:</strong> ${escapeHtml(patient.mobile)}</div>
                <div><strong>Member Number:</strong> ${escapeHtml(patient.memberNo)}</div>
                <div><strong>ID:</strong> ${escapeHtml(patient.SAID)}</div>
                <div><strong>Referring Doctor:</strong> ${escapeHtml(patient.referringDoctor)} ${escapeHtml(patient.referringDrNo)}</div>
                <div><strong>Date:</strong> ${escapeHtml(dateString)}</div>
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding:8px 24px 18px 24px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
                <tbody>
                  ${supplRows1Html}
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:0 24px 18px 24px;">
              <table role="table" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;table-layout:fixed;">
                <thead>
                  <tr>
                    ${prodHeaderHtml}
                  </tr>
                </thead>
                <tbody>
                  ${safeProdRowsHtml}
                </tbody>
              </table>
            </td>
          </tr>
          ${supplRows2SectionHtml}
          ${imagesHtml}
        </table>
      </body>
    </html>
  `
}
